import {
  Button,
  ButtonColor,
  ButtonSize,
  ButtonWidth,
  compact,
  IconCheck,
  IconChevronRight,
  IconPhone,
  Modal,
  ModalContent,
  type ModalHook,
  ModalSize,
  useControlledModalState,
} from '@leland-dev/leland-ui-library';
import { type GraphQLFormattedError } from 'graphql';
import { type FC, type FormEvent, useState } from 'react';

import RocketIcon from '../assets/svgs/illustrations/rocket.svg';
import { type Category } from '../types/cmsTypes';
import phog from '../utils/analytics/posthog/posthog';
import { DEFAULT_ERROR_MESSAGE, NUM_CUSTOMERS_STR } from '../utils/constants';
import { sanityCategoryIdToUrn } from '../utils/sanity';
import { getUrlString } from '../utils/url';

import { useSubscribeModalSendSeoEmailCollectionMutation } from './__generated-gql-types__/SubscribeModal.generated';

interface SubscribeModalHookProps {
  categories?: Category[];
  didSubscribe: boolean;
  setDidSubscribe: (didSubscribe: boolean) => void;
  onClose: () => void;
}

interface SubscribeModalProps extends SubscribeModalHookProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const SubscribeModalContent: FC<SubscribeModalProps> = ({
  open,
  setOpen: setOpenProp,
  categories,
  didSubscribe,
  setDidSubscribe,
  onClose,
}) => {
  const setOpen = (open: boolean) => {
    setOpenProp(open);
    if (!open) {
      onClose();
    }
  };

  const [email, setEmail] = useState<string>();
  const trimmedEmail = email?.trim();

  const [sendSeoEmailCollectionAction, { loading: isSubscribing }] =
    useSubscribeModalSendSeoEmailCollectionMutation();
  const [subscribeModalError, setSubscribeModalError] =
    useState<readonly GraphQLFormattedError[]>();

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!trimmedEmail) {
      setOpen(false);
    }

    const categoryList = categories
      ?.map((category) =>
        category._id ? sanityCategoryIdToUrn(category._id) : null,
      )
      .filter(compact);

    const articleUrl = window.location.href;

    const { errors } = await sendSeoEmailCollectionAction({
      variables: {
        email: trimmedEmail ?? '',
        categories: categoryList,
        articleUrl,
      },
    });

    phog.capture('Article - Subscribe', {
      categories: categoryList,
      articleUrl,
    });

    if (errors) {
      // something went wrong please try again?
      setSubscribeModalError(errors);
    } else {
      setSubscribeModalError([]);
      setDidSubscribe(true);
    }
  };

  return (
    <Modal open={open} onOpenChange={setOpen}>
      <ModalContent size={ModalSize.SMALL} hideCloseButton={!didSubscribe}>
        {didSubscribe ? (
          <>
            <HeaderSection
              alignLeft
              icon={
                <div className="rounded-full bg-leland-primary-extra-light p-2.5">
                  <IconCheck className="size-4 shrink-0 text-leland-primary" />
                </div>
              }
              title="You have successfully subscribed."
              description={`We'll keep you in the loop and won't share your email.`}
            />
            <div className="w-full space-y-3 p-6">
              <div>
                <h4 className="font-medium text-leland-gray-dark">
                  Chat with our team
                </h4>
                <p className="text-lg text-leland-gray-light">
                  Set up a call with a Leland team member to get exclusive deals
                  and help you find the perfect coach.
                </p>
              </div>
              <a
                className="block w-full"
                href={getUrlString(
                  'https://calendly.com/d/cpr6-m6z-yjh/meet-with-our-team',
                  trimmedEmail ? { email: trimmedEmail } : undefined,
                )}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button
                  type="button"
                  label="Schedule a call"
                  LeftIcon={IconPhone}
                  width={ButtonWidth.FULL}
                  buttonColor={ButtonColor.PRIMARY}
                  size={ButtonSize.MEDIUM}
                  onClick={() => {
                    phog.capture('Article - Schedule Call', {
                      email: trimmedEmail,
                    });
                    setOpen(false);
                  }}
                />
              </a>
            </div>
          </>
        ) : (
          <>
            <HeaderSection
              icon={<RocketIcon className="size-24 sm:block" />}
              title="Stay in the loop. Go places."
              description={`Join ${NUM_CUSTOMERS_STR} others and receive free resources, exclusive event invites, and personalized support to reach your goals.`}
            />
            <div className="flex w-full flex-col items-center justify-center space-y-4 p-6 sm:space-y-6 sm:p-8">
              <form
                className="w-full space-y-4 sm:space-y-6"
                onSubmit={onSubmit}
              >
                <input
                  className="w-full rounded-md border border-leland-gray-stroke p-3 text-leland-gray-dark"
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.currentTarget.value)}
                  placeholder="Type your email..."
                />
                {(subscribeModalError?.length ?? 0) > 0 ? (
                  <p className="rounded px-2 text-leland-red">
                    {process.env.NEXT_PUBLIC_APPLICATION_ENV === 'production'
                      ? DEFAULT_ERROR_MESSAGE
                      : subscribeModalError
                          ?.map((val) => val.message)
                          .join('\n')}
                  </p>
                ) : null}
                <Button
                  type="submit"
                  label={isSubscribing ? 'Subscribing...' : 'Subscribe'}
                  disabled={!trimmedEmail || isSubscribing}
                  buttonColor={ButtonColor.PRIMARY}
                  width={ButtonWidth.FULL}
                />
              </form>
              <button
                className="flex items-center justify-center text-leland-gray-dark"
                onClick={() => setOpen(false)}
              >
                <span>Continue reading</span>
                <IconChevronRight className="ml-1 size-4" />
              </button>
            </div>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

const HeaderSection: React.FC<{
  alignLeft?: boolean;
  icon: React.ReactNode;
  title: string;
  description: string;
}> = ({ alignLeft, icon, title, description }) => (
  <div
    className={`flex w-full flex-col justify-center space-y-1 border-b border-leland-gray-stroke p-6 sm:space-y-3 sm:p-8 ${alignLeft ? 'items-start' : 'items-center text-center'}`}
  >
    {icon}
    <div className="space-y-2">
      <h3 className="text-xl font-medium text-leland-gray-dark">{title}</h3>
      <p className="text-lg text-leland-gray-light">{description}</p>
    </div>
  </div>
);

export const useSubscribeModal: ModalHook<SubscribeModalHookProps> = () => {
  const [open, setOpen] = useControlledModalState();
  const SubscribeModal: FC<SubscribeModalProps> = (props) => {
    return <SubscribeModalContent {...props} open={open} setOpen={setOpen} />;
  };
  return { open, setOpen, Modal: SubscribeModal };
};
